/** The single source for a comedian tag provider's ID format, firing
 * behaviour, and copy — read by the Pixels/Tags page (M1), the manager
 * validator + confirm-before-activate copy (M2), and the public loader's
 * render guards (M3). Never imports `@punchup/data` (`data` depends on this
 * package, not the reverse). */

export const COMEDIAN_TAG_PROVIDERS = [
  "meta_pixel",
  "tiktok_pixel",
  "google_tag",
] as const;

export type ComedianTagProvider = (typeof COMEDIAN_TAG_PROVIDERS)[number];

/** One event a provider is known to fire today, and exactly how. */
export interface ProviderTagEvent {
  name: string;
  when: string;
  call: string;
}

/** A destination a `google_tag` container's `config.destinations` can hold. */
export interface ProviderDestinationSpec {
  label: string;
  idPattern: RegExp;
  conversionLabelPattern?: RegExp;
}

export interface ProviderSpec {
  label: string;
  kind: "pixel" | "container";
  idLabel: string;
  idPattern: RegExp;
  idFormatHint: string;
  idExample: string;
  docsHint: string;
  events: ProviderTagEvent[];
  /** Only `google_tag`: the destination kinds its `config` can carry. */
  destinations?: Record<"ga4" | "google_ads", ProviderDestinationSpec>;
}

export const COMEDIAN_TAG_PROVIDER_REGISTRY: Record<
  ComedianTagProvider,
  ProviderSpec
> = {
  meta_pixel: {
    label: "Meta Pixel",
    kind: "pixel",
    idLabel: "Pixel ID",
    idPattern: /^[0-9]{15,17}$/,
    idFormatHint: "15-17 digit numeric pixel ID",
    idExample: "1576337914289091",
    docsHint: "Meta Events Manager > Data Sources > your pixel",
    events: [
      {
        name: "PageView",
        when: "on every public page load, scoped to this pixel",
        call: "fbq('trackSingle', <id>, 'PageView')",
      },
    ],
  },
  tiktok_pixel: {
    label: "TikTok Pixel",
    kind: "pixel",
    idLabel: "Pixel code",
    idPattern: /^[A-Z0-9]{20}$/,
    idFormatHint: "20-character uppercase alphanumeric pixel code",
    idExample: "DA77FGRC77U0QGTKJ8MG",
    docsHint: "TikTok Events Manager > your pixel > Pixel code",
    events: [
      {
        name: "page",
        when: "on every public page load via ttq.page()",
        call: "ttq.page() after ttq.load(<id>)",
      },
    ],
  },
  google_tag: {
    label: "Google Tag",
    kind: "container",
    idLabel: "Google tag ID",
    idPattern: /^(G|GT)-[A-Z0-9]{6,12}$/,
    idFormatHint: "G- or GT- tag ID, not a GTM- container ID",
    idExample: "G-19F332C6YN",
    docsHint: "Google Analytics / Google Ads > Admin > Data Streams",
    events: [
      {
        name: "page_view",
        when: "on every public page load via gtag('config')",
        call: "gtag('config', <id>)",
      },
    ],
    destinations: {
      ga4: {
        label: "GA4",
        idPattern: /^G-[A-Z0-9]{6,12}$/,
      },
      google_ads: {
        label: "Google Ads",
        idPattern: /^AW-[0-9]{9,11}$/,
        conversionLabelPattern: /^[A-Za-z0-9_-]{4,64}$/,
      },
    },
  },
};

/** Punchup's own tag IDs, which always load regardless of a comedian's own
 * tags (Meta has no Punchup-global pixel, so it is absent here). */
export const PUNCHUP_GLOBAL_TAGS: Record<
  Extract<ComedianTagProvider, "tiktok_pixel" | "google_tag">,
  string
> = {
  tiktok_pixel: "D6V9CHRC77UB4ENA931G",
  google_tag: "GTM-NS9ZXC4L",
};

export function isComedianTagProvider(
  value: unknown
): value is ComedianTagProvider {
  return (
    typeof value === "string" &&
    (COMEDIAN_TAG_PROVIDERS as readonly string[]).includes(value)
  );
}

/** The one normalization rule for a submitted ID, shared by the dashboard
 * extractor and the manager validator: trim, then Meta keeps digits only
 * and TikTok/Google upper-case. Does not validate — test `idPattern` after. */
export function normalizeComedianTagId(
  provider: ComedianTagProvider,
  raw: string
): string {
  const trimmed = raw.trim();
  return provider === "meta_pixel"
    ? trimmed.replace(/\D/g, "")
    : trimmed.toUpperCase();
}
